import { useEffect, useRef, useCallback, useState } from 'react';
import { useFrameworkWizardStore } from '../states';
import { uploadFileReq } from '@/shared/requests/upload-file';

export const useStepFourAutoSave = () => {
  const { stepFourData } = useFrameworkWizardStore();
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to upload response schema data using backend-provided URL directly
  const uploadResponseSchemaData = useCallback(async (responseSchemaData?: any[]) => {
    if (!stepFourData.responseSchemaUrl) {
      throw new Error('Response schema URL not found');
    }

    // Use provided data or fall back to default empty array
    const dataToUpload = responseSchemaData || [];
    
    if (!dataToUpload || dataToUpload.length === 0) {
      return;
    }

    // Create JSON blob
    const responseSchemaBlob = new Blob([JSON.stringify(dataToUpload, null, 2)], {
      type: 'application/json'
    });

    // Upload directly to the backend-provided URL (has RWD permissions)
    await uploadFileReq(stepFourData.responseSchemaUrl, responseSchemaBlob);
  }, [stepFourData.responseSchemaUrl]);

  // Debounced auto-save function
  const debouncedSave = useCallback((dataToSave: any[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      if (!stepFourData.responseSchemaUrl) {
        return;
      }

      try {
        setIsSaving(true);
        await uploadResponseSchemaData(dataToSave);
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show error notifications for auto-save failures
      } finally {
        setIsSaving(false);
      }
    }, 2000); // 2 second delay
  }, [stepFourData.responseSchemaUrl, uploadResponseSchemaData]);

  // Manual save function
  const saveNow = useCallback(async (dataToSave: any[]) => {
    if (!stepFourData.responseSchemaUrl) {
      throw new Error('Response schema URL not found');
    }

    try {
      setIsSaving(true);
      await uploadResponseSchemaData(dataToSave);
    } catch (error) {
      console.error('Manual save failed:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [stepFourData.responseSchemaUrl, uploadResponseSchemaData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    saveNow,
    isSaving,
    debouncedSave
  };
};
